import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Clock, MapPin } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { IMG, PHONE_TEL, PHONE_DISPLAY, ADDRESS } from "../lib/media";
import SectionHeading from "../components/site/SectionHeading";
import ReviewBadge from "../components/site/ReviewBadge";
import { ServiceIcon } from "../components/site/ServiceIcons";

const Home = () => {
  const { t } = useLang();

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden" data-testid="hero-section">
        <img
          src={IMG.hero}
          alt="Luxury kitchen remodel in Dallas by GA Signature"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-[#0A0A0C]" />
        <div className="absolute inset-0 grid-grain opacity-70" />

        <div className="relative container-x w-full pb-24 md:pb-32 pt-32">
          <img
            src="/logo.png"
            alt="GA Signature"
            className="h-40 sm:h-48 md:h-56 w-auto object-contain -ml-6 sm:-ml-8 mb-2 fade-up"
            style={{ animationDelay: "0.02s", mixBlendMode: "lighten" }}
            data-testid="hero-logo"
          />
          <span className="text-overline fade-up" style={{ animationDelay: "0.1s" }}>
            {t.hero.overline}
          </span>
          <h1
            className="mt-6 font-display font-light text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.02em] text-white max-w-5xl fade-up"
            style={{ animationDelay: "0.2s" }}
            data-testid="hero-title"
          >
            {t.hero.title}
          </h1>
          <p
            className="mt-6 max-w-2xl text-white/75 text-base md:text-lg leading-relaxed fade-up"
            style={{ animationDelay: "0.3s" }}
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3 fade-up" style={{ animationDelay: "0.45s" }}>
            <a href={`tel:${PHONE_TEL}`} className="btn-primary" data-testid="hero-call-btn">
              <Phone className="w-4 h-4" />
              {t.common.callNow} · {PHONE_DISPLAY}
            </a>
            <Link to="/contact" className="btn-ghost" data-testid="hero-estimate-btn">
              {t.common.requestEstimate}
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-4 fade-up" style={{ animationDelay: "0.6s" }}>
            <ReviewBadge />
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-pulse" />
              <span>{t.common.openNow}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="section-y border-t border-white/5" data-testid="about-section">
        <div className="container-x grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <SectionHeading overline={t.about.overline} title={t.about.title} />
          </div>
          <div className="lg:col-span-7 lg:pt-6">
            <p className="text-white/70 text-lg leading-relaxed">{t.about.body}</p>
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
              {[t.about.stat1, t.about.stat2, t.about.stat3, t.about.stat4].map((s, i) => (
                <div key={i} className="hairline rounded-lg p-5 bg-[#141417]" data-testid={`stat-${i}`}>
                  <div className="text-3xl font-display font-light text-white">{s.k}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/55 mt-2">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES TEASER */}
      <section className="section-y border-t border-white/5 bg-[#0C0C0F]" data-testid="services-teaser">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <SectionHeading overline={t.services.overline} title={t.services.title} subtitle={t.services.subtitle} />
            <Link to="/services" className="btn-ghost h-11 px-5 text-sm self-start md:self-auto" data-testid="services-view-all">
              {t.common.viewAll}
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.services.items.slice(0, 6).map((s, i) => (
              <div
                key={i}
                className="hairline hairline-hover rounded-xl p-7 bg-[#141417] card-lift"
                data-testid={`service-card-${i}`}
              >
                <ServiceIcon index={i} className="w-8 h-8 text-[#0055FF]" />
                <h3 className="mt-6 font-display text-xl text-white">{s.title}</h3>
                <p className="mt-3 text-white/60 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="section-y border-t border-white/5" data-testid="why-us-section">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading overline={t.whyUs.overline} title={t.whyUs.title} />
            <div className="mt-10 overflow-hidden rounded-xl hairline">
              <img src={IMG.livingRoom} alt="Premium dark living room" className="w-full h-[360px] object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {t.whyUs.points.map((p, i) => (
              <div key={i} className="hairline hairline-hover rounded-xl p-6 bg-[#141417] card-lift">
                <div className="font-display text-2xl text-[#0055FF] font-light">{p.k}</div>
                <p className="mt-3 text-white/70 leading-relaxed text-sm">{p.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="border-y border-white/5 bg-[#0C0C0F]">
        <div className="container-x py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="text-overline mb-3">{t.common.serveArea}</div>
            <h3 className="font-display text-2xl md:text-3xl font-light max-w-xl">
              Free, no-pressure estimate in 24–48 hours.
            </h3>
            <div className="mt-4 flex flex-wrap gap-4 text-sm text-white/60">
              <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-[#0055FF]" /> {t.common.openNow}</span>
              <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-[#0055FF]" /> {ADDRESS}</span>
            </div>
          </div>
          <div className="flex gap-3">
            <a href={`tel:${PHONE_TEL}`} className="btn-primary" data-testid="cta-call">
              <Phone className="w-4 h-4" />
              {t.common.callNow}
            </a>
            <Link to="/contact" className="btn-ghost" data-testid="cta-estimate">
              {t.common.requestEstimate}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
